import { useMutation } from '@tanstack/react-query';
import { operations } from '@/generated/supplier-experience-api';
import apiClient from '@/lib/apiClient';
import useDataDog from '@/reporting/DataDog';

type CreateQuoteResponse = operations['createQuote']['responses']['200']['content']['application/json'];

export type CreateMonduQuoteBody = operations['createQuote']['requestBody']['content']['application/json'];

export type MonduQuote = CreateQuoteResponse['data'];

/**
 * Creates a Mondu lending opportunity quote.
 *
 * SEA records `PRIVACY_POLICY` and `MONDU_DATA_SHARING_CONSENT` as part of this POST, so the caller
 * must only reach it once the supplier has consented — either in the Get A Quote modal or through an
 * acknowledgement `useMonduHandoffSkip` already found. SEA also stamps identity and `countryCode`, so
 * neither belongs on the body.
 */
const useCreateMonduQuote = () => {
  const { addError } = useDataDog();

  return useMutation({
    mutationKey: ['lending-opportunities', 'mondu', 'quote'],
    mutationFn: async (body: CreateMonduQuoteBody): Promise<MonduQuote> => {
      const response = await apiClient
        .post('api/supplier-experience/user/lending-opportunities/MONDU/quotes', { json: body })
        .json<CreateQuoteResponse>();

      if (!response.data?.quoteId) {
        throw new Error('SEA returned an invalid Mondu quote');
      }

      return response.data;
    },
    onError: (error: Error): void => {
      addError(error, { feature: 'useCreateMonduQuote' });
    },
  });
};

export default useCreateMonduQuote;
